import {
  AllocationStatus,
  isDefined,
  RegistrationMode,
} from "@timeedit/registration-schemas";
import { ReactNode, useMemo } from "react";
import { DescriptionDisabled, Translations, WaitlistState } from "./types";

type UseAllocationStateParams = {
  enableWaitlist: boolean;
  full: boolean;
  allocationStatus: AllocationStatus;
  mode: RegistrationMode;
  open: boolean;
  blockStudentConflict: boolean;
  clash: boolean;
  linkedValidationSuccess: boolean;
  isOnWaitlist: boolean;
  disabledState: DescriptionDisabled | undefined;
  hasStudentsInWaitlist: boolean;
  translations: Pick<Translations, "noSeatsLeft" | "closed" | "addToWaitlist">;
};

type UseAllocationStateResult = {
  waitlistState: WaitlistState;
  disabled: boolean;
  description: ReactNode;
};

const DISABLED_ON_CLOSED: Record<RegistrationMode, boolean> = {
  student: true,
  teacher: false,
};

export function useDisabledWaitlistState({
  enableWaitlist,
  full,
  allocationStatus,
  mode,
  open,
  blockStudentConflict,
  clash,
  linkedValidationSuccess,
  isOnWaitlist,
  disabledState,
  hasStudentsInWaitlist,
  translations,
}: UseAllocationStateParams): UseAllocationStateResult {
  const waitlistState = useMemo<WaitlistState>(() => {
    if (!enableWaitlist) {
      return {
        shouldShowWaitlist: false,
      };
    }

    const shouldShowWaitlist =
      (full || hasStudentsInWaitlist) &&
      (allocationStatus === "NOT_ALLOCATED" ||
        allocationStatus === "ALLOCATED_TO_OTHER");

    return {
      // If we should consider showing waitlist related information on the button
      shouldShowWaitlist,
    };
  }, [enableWaitlist, full, hasStudentsInWaitlist, allocationStatus]);

  const conflictDisable = useMemo(() => {
    if (
      mode === "student" &&
      blockStudentConflict &&
      clash &&
      // We always want to have leave/join waitlist buttons enabled regardless of student conflicts
      !waitlistState.shouldShowWaitlist
    ) {
      return true;
    }
    return false;
  }, [blockStudentConflict, clash, waitlistState.shouldShowWaitlist, mode]);

  const { disabled, description } = useMemo<DescriptionDisabled>(() => {
    const isClosed = !open;
    const shouldDisableOnClosed = DISABLED_ON_CLOSED[mode];
    const isClosedAndShouldDisable = isClosed && shouldDisableOnClosed;

    const closedResult = (
      disabledOverride: boolean = shouldDisableOnClosed
    ) => ({
      disabled: disabledOverride,
      description: translations.closed,
    });

    // Override
    if (isDefined(disabledState?.disabled) && disabledState.disabled) {
      return disabledState;
    }

    // Student conflict blocking
    if (conflictDisable) {
      return { disabled: true, description: null };
    }

    // Linked validation
    if (!linkedValidationSuccess) {
      return { disabled: true, description: translations.noSeatsLeft };
    }

    // ALLOCATED_TO_THIS or MULTIPLE_ALLOCATIONS - no waitlist logic
    if (
      allocationStatus === "ALLOCATED_TO_THIS" ||
      allocationStatus === "MULTIPLE_ALLOCATIONS"
    ) {
      return isClosed ? closedResult() : { disabled: false, description: null };
    }

    // Full group without waitlist enabled
    if (!enableWaitlist && full) {
      return isClosedAndShouldDisable
        ? closedResult(true)
        : { disabled: true, description: translations.noSeatsLeft };
    }

    // (NOT_ALLOCATED || ALLOCATED_TO_OTHER) + (full || has students in waitlist)
    if (waitlistState.shouldShowWaitlist) {
      const waitlistDescription = isOnWaitlist
        ? null
        : translations.addToWaitlist;
      return isClosedAndShouldDisable
        ? closedResult(true)
        : { disabled: false, description: waitlistDescription };
    }

    // NOT_ALLOCATED or ALLOCATED_TO_OTHER (not full)
    if (
      allocationStatus === "NOT_ALLOCATED" ||
      allocationStatus === "ALLOCATED_TO_OTHER"
    ) {
      return isClosed ? closedResult() : { disabled: false, description: null };
    }

    return { disabled: false, description: null };
  }, [
    conflictDisable,
    disabledState,
    mode,
    linkedValidationSuccess,
    allocationStatus,
    enableWaitlist,
    full,
    waitlistState.shouldShowWaitlist,
    translations.noSeatsLeft,
    translations.closed,
    translations.addToWaitlist,
    open,
    isOnWaitlist,
  ]);

  return { waitlistState, disabled, description };
}
